import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { useSnapshot } from 'valtio'
import { packetsReplayState } from '../../state/packetsReplayState'
import TopMenuIconsGL, { type GlIcon } from './TopMenuIconsGL'
import styles from './TopMenu.module.css'

/**
 * Top-center command bar — the replay viewer's own console.
 *
 * A compact, ICON-ONLY strip in the replay HUD language: dark navy metal,
 * square edges, restrained light, and gold only for the primary utility accent.
 * Playback (play/pause/restart/seek) lives in the bottom bar, so this strip
 * only toggles HUD surfaces + opens settings/help. The keyboard glyph opens
 * the detailed hotkeys reference below.
 *
 * SECTIONS is the single source of truth for the hotkey reference list.
 * Lives inside #ui-root (already scaled by --guiScale), so sizes use --ui-scale.
 */

type Key = {
  keys: string[]
  sep?: '+' | '/'
  seps?: Array<'+' | '/'>
  desc: string
  hint?: string
  accent?: 'gold' | 'cyan'
}
type Section = { label: string; items: Key[] }
type Props = {
  showPackets: boolean
  onTogglePackets: () => void
  onOpenSettings: () => void
}

// "Hide UI" declutter — collapse the panels (info / chat / telemetry) and
// minimize the bottom timeline together. Each panel mirrors these flags via
// inward sync.
function collapseHud (collapse: boolean) {
  packetsReplayState.infoViewCollapsed = collapse
  packetsReplayState.chatCollapsed = collapse
  packetsReplayState.telemetryCollapsed = collapse
  packetsReplayState.bottomBarCollapsed = collapse
}

function isHudCollapsed () {
  return packetsReplayState.infoViewCollapsed
    && packetsReplayState.chatCollapsed
    && packetsReplayState.telemetryCollapsed
    && packetsReplayState.bottomBarCollapsed
}

const SECTIONS: Section[] = [
  {
    label: 'Воспроизведение',
    items: [
      { keys: ['Space'], desc: 'Пауза / Воспроизведение', hint: 'Переключатель' },
      { keys: ['R'], desc: 'Перезапуск с начала', hint: 'С нулевой отметки', accent: 'gold' },
      { keys: ['Shift', '←', '→'], seps: ['+', '/'], desc: 'Перемотка ±10 сек', hint: 'Назад / вперёд' },
    ],
  },
  {
    label: 'Навигация',
    items: [
      { keys: ['←', '→'], sep: '/', desc: 'Пред. / след. событие', hint: 'Прыжок по событиям' },
      { keys: ['F5'], desc: 'Вид от третьего лица', hint: 'Внешняя камера', accent: 'cyan' },
    ],
  },
  {
    label: 'Интерфейс',
    items: [
      { keys: ['P', 'T'], sep: '/', desc: 'Список пакетов', hint: 'Показать / скрыть' },
      { keys: ['Tab'], desc: 'Список игроков', hint: 'Поверх экрана', accent: 'gold' },
      { keys: ['I'], desc: 'Свернуть HUD', hint: 'Панели, чат и таймлайн' },
      { keys: ['Esc'], desc: 'Настройки', hint: 'Меню настроек' },
    ],
  },
  {
    label: 'Жесты (мобильные)',
    items: [
      { keys: ['↕'], desc: 'Свайп вниз', hint: 'Открыть настройки' },
      { keys: ['↕'], desc: 'Свайп вверх', hint: 'Закрыть настройки' },
    ],
  },
]

const TOTAL = SECTIONS.reduce((n, s) => n + s.items.length, 0)

export default function TopMenu ({ showPackets, onTogglePackets, onOpenSettings }: Props) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const { infoViewCollapsed, chatCollapsed, telemetryCollapsed, bottomBarCollapsed } = useSnapshot(packetsReplayState)
  const hudCollapsed = infoViewCollapsed && chatCollapsed && telemetryCollapsed && bottomBarCollapsed

  const openSettings = useCallback(() => {
    setOpen(false)
    collapseHud(false)
    onOpenSettings()
  }, [onOpenSettings])

  // Icon-only toolbar (crisp SVG + composited glow — see TopMenuIconsGL). Playback is
  // owned by the bottom bar; this strip only toggles HUD surfaces and opens
  // settings/help. Each icon carries the keyboard shortcut as a corner label.
  const glIcons: GlIcon[] = [
    {
      key: 'packets',
      icon: 'packets',
      title: showPackets ? 'Скрыть пакеты' : 'Показать пакеты',
      keyLabel: 'P',
      active: showPackets,
      onClick () { setOpen(false); onTogglePackets() },
    },
    {
      key: 'hud',
      icon: hudCollapsed ? 'hudHidden' : 'hud',
      title: hudCollapsed ? 'Показать панели' : 'Скрыть панели',
      keyLabel: 'I',
      active: hudCollapsed,
      onClick () { setOpen(false); collapseHud(!hudCollapsed) },
    },
    {
      key: 'hotkeys',
      icon: 'hotkeys',
      title: 'Горячие клавиши',
      active: open,
      expanded: open,
      onClick: () => setOpen(v => !v),
    },
    {
      key: 'settings',
      icon: 'settings',
      title: 'Настройки',
      keyLabel: 'Esc',
      onClick: openSettings,
    },
  ]

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Close panel on Escape
      if (open && e.key === 'Escape') {
        setOpen(false)
        return
      }
      // Don't handle shortcuts when panel is open or typing
      if (open || (e.target instanceof HTMLInputElement) || (e.target instanceof HTMLTextAreaElement)) return

      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        openSettings()
        return
      }

      switch (e.key.toLowerCase()) {
        case 'p':
        case 't':
          onTogglePackets()
          break
        case 'i':
          collapseHud(!isHudCollapsed())
          break
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onTogglePackets, openSettings])

  // Click outside to close
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      if (e.target instanceof Node && !rootRef.current?.contains(e.target)) setOpen(false)
    }
    document.addEventListener('pointerdown', onDown, true)
    return () => document.removeEventListener('pointerdown', onDown, true)
  }, [open])

  return (
    <div
      ref={rootRef}
      className={styles.root}
      data-replay-ui
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <TopMenuIconsGL icons={glIcons} />

      {/* ── Hotkeys reference (opens below the bar) ── */}
      <div
        className={[styles.panel, open && styles.panelOpen].filter(Boolean).join(' ')}
        role="dialog"
        aria-label="Горячие клавиши"
        aria-hidden={!open}
      >
        <div className={styles.panelSurface}>
          {/* header */}
          <div className={styles.panelHeader}>
            <span className={`${styles.panelHeaderIcon} pixelart-icons-font-keyboard`} aria-hidden="true" />
            <span className={styles.panelHeaderTitle}>Горячие клавиши</span>
            <button
              type="button"
              className={styles.panelClose}
              aria-label="Закрыть"
              onClick={() => setOpen(false)}
            >
              ×
            </button>
          </div>

          {/* sections */}
          {SECTIONS.map(({ label, items }) => (
            <div key={label} className={styles.section}>
              <div className={styles.sectionHeader}>
                <span className={styles.sectionLabel}>
                  <span className={styles.sectionDot} aria-hidden="true" />
                  {label}
                </span>
                <span className={styles.sectionCount}>{items.length} клав.</span>
              </div>
              {items.map(({ keys, sep, seps, desc, hint, accent }) => {
                const separator = sep ?? (keys.length > 1 ? '+' : '')
                return (
                  <div key={desc} className={styles.row}>
                    <span className={styles.keys}>
                      {keys.map((k, i) => (
                        <Fragment key={k}>
                          {i > 0 && (seps?.[i - 1] ?? separator) && (
                            <span className={styles.keysSep} aria-hidden="true">{seps?.[i - 1] ?? separator}</span>
                          )}
                          <kbd className={[
                            styles.key,
                            accent === 'gold' ? styles.keyGold : '',
                            accent === 'cyan' ? styles.keyCyan : '',
                            k.length > 3 ? styles.keyWide : '',
                          ].filter(Boolean).join(' ')}>{k}</kbd>
                        </Fragment>
                      ))}
                    </span>
                    <span className={styles.descCell}>
                      <span className={styles.desc}>{desc}</span>
                      {hint && <span className={styles.hint}>{hint}</span>}
                    </span>
                  </div>
                )
              })}
            </div>
          ))}

          {/* footer */}
          <div className={styles.panelFooter}>
            <span className={styles.panelFooterLeft}>
              <span className={styles.footerDot} aria-hidden="true" />
              {TOTAL} команд
            </span>
            <span className={styles.panelFooterRight}>Реплей-движок</span>
          </div>
        </div>
      </div>
    </div>
  )
}
